import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, RotateCcw, Volume2, AlertCircle } from 'lucide-react';

interface VoiceMessagePlayerProps {
  src: string;
  isCustomer?: boolean;
  duration?: number; // Optional duration in seconds if provided by backend
  className?: string;
}

function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VoiceMessagePlayer({
  src,
  isCustomer = false,
  duration: initialDuration,
  className = '',
}: VoiceMessagePlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState<number>(initialDuration || 0);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [hasError, setHasError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Sync duration if passed as prop
  useEffect(() => {
    if (initialDuration && (!totalDuration || isNaN(totalDuration))) {
      setTotalDuration(initialDuration);
    }
  }, [initialDuration, totalDuration]);

  // Audio lifecycle
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
      }
      setHasError(false);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setHasError(true);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [isDragging]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.warn('Playback prevented or audio load error', err);
        setHasError(true);
        setIsPlaying(false);
      }
    }
  }, [isPlaying]);

  const handleSpeedToggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const speeds = [1, 1.5, 2];
    const nextIndex = (speeds.indexOf(playbackRate) + 1) % speeds.length;
    const nextSpeed = speeds[nextIndex];
    audio.playbackRate = nextSpeed;
    setPlaybackRate(nextSpeed);
  }, [playbackRate]);

  // Seek calculation
  const seekToPosition = useCallback(
    (clientX: number) => {
      const bar = progressBarRef.current;
      const audio = audioRef.current;
      if (!bar || !audio || !totalDuration || isNaN(totalDuration)) return;

      const rect = bar.getBoundingClientRect();
      const clickPos = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = clickPos / rect.width;
      const newTime = percentage * totalDuration;

      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [totalDuration]
  );

  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    seekToPosition(e.clientX);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    seekToPosition(e.clientX);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      seekToPosition(moveEvent.clientX);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const touch = e.touches[0];
    if (touch) seekToPosition(touch.clientX);

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const t = moveEvent.touches[0];
      if (t) seekToPosition(t.clientX);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  const progressPercent =
    totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;

  if (hasError && !src) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 text-xs opacity-75">
        <AlertCircle className="w-4 h-4 text-red-500" />
        <span>Audio unavailable</span>
      </div>
    );
  }

  // Styles based on whether it is in a customer bubble (white background) or admin bubble (blue background)
  const isLight = isCustomer;

  return (
    <div
      className={`voice-player flex items-center gap-3 py-1.5 px-1 select-none w-full min-w-[260px] xs:min-w-[280px] sm:min-w-[320px] max-w-[380px] ${className}`}
      role="region"
      aria-label="Voice message player"
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play / Pause Circular Button */}
      <button
        type="button"
        onClick={togglePlay}
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 transition-transform active:scale-95 shadow-sm focus:outline-none ${
          isLight
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-white text-blue-600 hover:bg-blue-50'
        }`}
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
        ) : (
          <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current translate-x-0.5" />
        )}
      </button>

      {/* Progress & Time Container */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
        {/* Interactive Progress Bar */}
        <div
          ref={progressBarRef}
          onClick={handleBarClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          className="relative w-full h-4 flex items-center cursor-pointer group"
          role="slider"
          aria-valuenow={currentTime}
          aria-valuemin={0}
          aria-valuemax={totalDuration}
          aria-label="Voice seek bar"
        >
          {/* Background track */}
          <div
            className={`w-full h-2 rounded-full overflow-hidden transition-all group-hover:h-2.5 ${
              isLight ? 'bg-gray-200' : 'bg-white/25'
            }`}
          >
            {/* Filled progress */}
            <div
              className={`h-full rounded-full transition-all duration-75 ${
                isLight ? 'bg-blue-600' : 'bg-white'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Scrubber Knob */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full shadow-md transition-transform scale-90 group-hover:scale-110 ${
              isLight ? 'bg-blue-700 border-2 border-white' : 'bg-white border-2 border-blue-500'
            }`}
            style={{ left: `${progressPercent}%` }}
          />
        </div>

        {/* Time display & Speed control */}
        <div
          className={`flex items-center justify-between text-[11px] font-mono leading-none ${
            isLight ? 'text-gray-500' : 'text-white/90'
          }`}
        >
          <span>{formatDuration(currentTime)}</span>
          <div className="flex items-center gap-2">
            <span>{formatDuration(totalDuration)}</span>

            {/* Playback speed toggle */}
            <button
              type="button"
              onClick={handleSpeedToggle}
              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors focus:outline-none ${
                isLight
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              title="Change playback speed"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
